import React, { useState, useEffect } from 'react';
import useAppwrite from '../../../lib/useAppwrite';
import { config, deleteDocument, getAllDocuments, getCurrentUser } from '../../../lib/appwrite';
import { useStateContext } from '../../../contexts/ContextProvider';
import { FaCirclePlus, FaTrashCan } from "react-icons/fa6";
import { FaRegEdit } from "react-icons/fa";
import EditUser from './EditUser';
import ConfirmDelete from '../../../components/ConfirmDelete';

const Users = () => {
  const { data: user } = useAppwrite(() => getCurrentUser());
  const { currentColor } = useStateContext();
  const [createRecord, setCreateRecord] = useState(false);
  const [editRecord, setEditRecord] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [users, setUsers] = useState([]);

  // Data
  const { data, isLoading, refetch } = useAppwrite(
    () => getAllDocuments(config.userCollectionId)
  );

  useEffect(() => {
    if (data) {
      setUsers(data);
    }
  }, [data]);

  useEffect(() => {
    if (editRecord === false && createRecord === false) {
      refetch();
    }
  }, [editRecord, createRecord]);

  const handleRowSelect = (user) => {
    setSelectedRowData(user);
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    await deleteDocument(selectedRowData.$id || selectedRowData.id, config.userCollectionId);
    refetch();
    setSelectedRowData(null);
  };

  const getPermissionColor = (permissions) => {
    switch (permissions) {
      case 'admin':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      case 'user':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">User Management</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage user accounts and permissions
            </p>
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedRowData(null);
              setCreateRecord(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-sm"
          >
            <FaCirclePlus />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">No users found.</p>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedRowData(null);
                setCreateRecord(true);
              }}
              className="mt-4 text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Add your first user
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <tr
                    key={user.id || user.$id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
                      selectedRowData?.id === user.id || selectedRowData?.$id === user.$id
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : ''
                    }`}
                    onClick={() => handleRowSelect(user)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.username}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPermissionColor(
                          user.permissions
                        )}`}
                      >
                        {user.permissions || 'user'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRowData(user);
                            setEditRecord(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          title="Edit"
                        >
                          <FaRegEdit />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRowData(user);
                            setConfirmDelete(true);
                          }}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Delete"
                        >
                          <FaTrashCan />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit/Create Modal */}
      {createRecord && (
        <EditUser
          view={true}
          setView={(val) => {
            setCreateRecord(false);
            setEditRecord(false);
          }}
          selectedRowData={null}
          setSelectedRowData={setSelectedRowData}
          mode="create"
          onSuccess={() => {
            setCreateRecord(false);
            setEditRecord(false);
            setSelectedRowData(null);
            refetch();
          }}
        />
      )}
      {editRecord && (
        <EditUser
          view={true}
          setView={(val) => {
            setCreateRecord(false);
            setEditRecord(false);
          }}
          selectedRowData={selectedRowData}
          setSelectedRowData={setSelectedRowData}
          mode="edit"
          onSuccess={() => {
            setCreateRecord(false);
            setEditRecord(false);
            setSelectedRowData(null);
            refetch();
          }}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDelete
        title="Confirm Deletion?"
        text="Are you sure you want to delete this user? This action cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        visible={confirmDelete}
        setVisible={setConfirmDelete}
        handleConfirm={handleDelete}
      />
    </div>
  );
};

export default Users;

